import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchRevenueData } from '../services/dashboardService';
import { fyStartYear } from '../utils/dateUtils';

const FY_MONTHS = ['JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN'];

/**
 * Maps a calendar month (0-11) to its position in the AU financial year (0-11).
 * Jul=0, Aug=1, ..., Dec=5, Jan=6, ..., Jun=11
 */
const toFyMonthIndex = (calendarMonth) => (calendarMonth + 6) % 12;

/**
 * Fetches invoice revenue data and aggregates it by Australian financial year
 * and month, returning chart-ready data for up to 4 FYs (current + 3 prior).
 */
export default function useRevenueData() {
  const [rawInvoices, setRawInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchRevenueData();
      setRawInvoices(data);
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentFyStart = useMemo(() => fyStartYear(new Date()), []);

  const fyKeys = useMemo(
    () => Array.from({ length: 4 }, (_, i) => `FY${currentFyStart - 3 + i}`),
    [currentFyStart],
  );

  const chartData = useMemo(() => {
    const grid = FY_MONTHS.map((month) => {
      const row = { month };
      fyKeys.forEach((fy) => { row[fy] = 0; });
      return row;
    });

    for (const inv of rawInvoices) {
      const date = new Date(inv.invoice_date);
      const fy = fyStartYear(date);
      const key = `FY${fy}`;

      if (!fyKeys.includes(key)) continue;

      const monthIdx = toFyMonthIndex(date.getMonth());
      grid[monthIdx][key] = (grid[monthIdx][key] || 0) + parseFloat(inv.total_amount || 0);
    }

    return grid;
  }, [rawInvoices, fyKeys]);

  return { chartData, fyKeys, loading, error, reload: load };
}
